import React from "react";
import { Text } from "@flixbus/honeycomb-react";
import type { AiJobStatus } from "../../../hooks/useAiDraft";
import { toStatusCopy, progressPatienceHint, formatElapsed } from "./chatCopy";
import styles from "./ChatMessage.module.scss";

export interface ProgressBodyProps {
  status: AiJobStatus;
}

/** Running bubble: animated dots + live status, plus an elapsed-time counter and
 *  a patience hint — so a long parse/mapping wait reads as "working", not "hung".
 *  The ticker lives here (not in the pure mapper) and resets whenever the status
 *  changes, i.e. per backend phase. */
const ProgressBody: React.FC<ProgressBodyProps> = ({ status }) => {
  const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

  React.useEffect(() => {
    setElapsedSeconds(0);
    const started = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [status]);

  const hint = progressPatienceHint(elapsedSeconds);

  return (
    <div className={styles.typing} aria-live="polite" aria-busy={true}>
      <span className={styles.typingDots} aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <div>
        <Text small extraClasses={styles.typingStatus}>
          {toStatusCopy(status)} ({formatElapsed(elapsedSeconds)})
        </Text>
        {hint && (
          <Text small extraClasses={styles.typingHint}>
            {hint}
          </Text>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProgressBody);
